import { createEnv } from "@t3-oss/env-core";
import { z } from "zod";

/**
 * Build-time-validated env. Every var is prefixed `VITE_` so Vite exposes
 * it on `import.meta.env`. All values end up in the production JS bundle —
 * treat them as PUBLIC.
 */
export const env = createEnv({
  clientPrefix: "VITE_",
  client: {
    VITE_ANTHROPIC_API_KEY: z.string().optional(),
    VITE_POWER_USER_PASSPHRASE: z.string().min(1, "VITE_POWER_USER_PASSPHRASE must be set"),
    // GitHub Contents API persistence — the LEGACY blob backend (Phase 6).
    //
    // Superseded in production by the BFF / Azure backend (see
    // VITE_WORKSHOP_BACKEND below); kept as a dormant, PAT-driven fallback for
    // the dev/POC path. When token + owner + repo are all set,
    // `isGithubSyncEnabled()` returns true and `workshop-blob-sync.ts` routes
    // through the GitHub adapter — but only when the BFF backend isn't enabled,
    // which always wins. Production bakes no GitHub creds, so this is off there;
    // when all three are missing the stores fall back to IDB-only.
    //
    // `VITE_GITHUB_TOKEN` is a PAT with `repo` scope. See
    // `docs/adr/0010-github-persistence.md` for the legacy design.
    VITE_GITHUB_HOST: z.string().optional(),
    VITE_GITHUB_OWNER: z.string().optional(),
    VITE_GITHUB_REPO: z.string().optional(),
    VITE_GITHUB_BRANCH: z.string().default("main"),
    VITE_GITHUB_TOKEN: z.string().optional(),

    // Auth mode switch. "zitadel" = OIDC + PKCE against infra/zitadel/.
    // "seed" = legacy plaintext roster in content/users.ts (offline /
    // air-gapped dev). Defaults to "zitadel" — production runs
    // against the deployed ZITADEL instance. Smoke tests override
    // to "seed" via `playwright.config.ts` so they don't need the
    // OIDC redirect dance.
    VITE_AUTH_MODE: z.enum(["zitadel", "seed"]).default("zitadel"),
    VITE_ZITADEL_ISSUER: z.string().url().default("http://localhost:8080"),
    // Default is the deployed XAES Dashboard OIDC client id (from
    // kv-aiw-dev/aiw-zitadel-client-id). Builds MUST pass the real value via
    // --build-arg VITE_ZITADEL_CLIENT_ID; an out-of-date default surfaces as
    // ZITADEL "Errors.App.NotFound" (400) at the authorize redirect, and the
    // BFF must validate the SAME id as its audience (ZITADEL_CLIENT_ID).
    VITE_ZITADEL_CLIENT_ID: z.string().default("377621245035815982"),
    // Project ID for "XAES Mission Control" in the ZITADEL Console. Used
    // for project-role grants in Pass 2's Manage Crew CRUD. Optional —
    // omit if you only need sign-in, fill in to enable role assignment.
    VITE_ZITADEL_PROJECT_ID: z.string().optional(),
    // Email domain used to synthesize the email for users added via
    // Manage Crew. We collect a `username` from the facilitator; Zitadel
    // needs an email. Result: <username>@<domain>.
    VITE_ZITADEL_EMAIL_DOMAIN: z.string().default("xaes.local"),

    // Document-storage BFF origin (interim doc-storage feature). Empty =
    // same-origin, the common case: in prod nginx proxies `/api/*` to the
    // BFF, and in dev the Vite proxy forwards `/api` server-side. Set to an
    // absolute origin only when pointing the SPA at a BFF on another host.
    // The SPA attaches its ZITADEL access token as a Bearer header on every
    // BFF call; the BFF validates it and is the real authz boundary.
    VITE_DOCS_BFF_ORIGIN: z.string().default(""),

    // Where per-workshop content (structured records + captured blobs) lives.
    // "bff" routes records + blobs to the document-storage BFF under the
    // `workshops` tier — Azure Blob Storage under the BFF's managed identity,
    // sign-in required, IDB kept as a read/offline cache. This is the
    // PRODUCTION backend: the Dockerfile bakes `VITE_WORKSHOP_BACKEND=bff`.
    // "idb" keeps the R1 local-only posture: IndexedDB-backed, offline-capable,
    // no sign-in. It stays the Zod default here so local dev, vitest, and the
    // seed-mode smoke tests (none of which build the Docker image) run offline.
    VITE_WORKSHOP_BACKEND: z.enum(["idb", "bff"]).default("idb"),

    // Multi-client tenancy (multi-client plan). "on" = the BFF scopes all
    // workshop data per client: facilitators pick an active client (sent as
    // X-Client-Id on every BFF call) and manage per-client library shares;
    // client users are scoped server-side from their token. "off" (default)
    // = today's single-instance behaviour, byte-identical — no header, no
    // picker, unscoped caches. Flip only against a BFF running
    // MC_MULTI_CLIENT=true.
    VITE_MULTI_CLIENT: z.enum(["off", "on"]).default("off"),
    // ZITADEL org id of the DXC (facilitation) organisation. Mirrors the
    // BFF's MC_DXC_ORG_ID so the SPA can gate the client picker to DXC
    // home users without calling /api/clients first. Public (baked into
    // the bundle). Default = live kv-aiw-dev DXC org.
    VITE_DXC_ORG_ID: z.string().default("377620599834519021"),

    // XAES Analyst chat backend. "anthropic" (default) = browser-direct
    // Anthropic call via the AI SDK (today's behaviour). "agent" routes the
    // chat through the BFF to an AIW A2A general_blueprint agent
    // (`/api/agent/chat`) — see plans/AIW_AGENT_CHAT_INTEGRATION.md. The
    // agent path keeps the API key server-side and grounds on the
    // context-engine. Build-time switch: the SPA is a static bundle, so this
    // is set per build/deploy, not at runtime.
    VITE_CHAT_BACKEND: z.enum(["anthropic", "agent"]).default("anthropic"),
  },
  runtimeEnv: import.meta.env,
  emptyStringAsUndefined: true,
});

export const isClaudeConfigured = (): boolean =>
  Boolean(env.VITE_ANTHROPIC_API_KEY && env.VITE_ANTHROPIC_API_KEY.length > 0);

/** True when the XAES Analyst chat is wired to the AIW A2A agent (via BFF)
 *  rather than the browser-direct Anthropic call. */
export const isAgentChatBackend = (): boolean =>
  env.VITE_CHAT_BACKEND === "agent";

/**
 * Legacy (Phase 6) — true when token + owner + repo are all populated, so the
 * GitHub Contents API adapter (`src/lib/github-storage.ts`) can talk to a real
 * repo. This is the FALLBACK blob backend: `workshop-blob-sync.ts` consults it
 * only when the production BFF backend (`isWorkshopBffEnabled()`) is off — the
 * BFF always wins. Production bakes no GitHub creds, so this is false there.
 *
 * Branch is _not_ part of the check — it has a default of `"main"` and
 * is always populated.
 */
export const isGithubSyncEnabled = (): boolean =>
  Boolean(
    env.VITE_GITHUB_TOKEN &&
      env.VITE_GITHUB_OWNER &&
      env.VITE_GITHUB_REPO,
  );

export const isZitadelMode = (): boolean => env.VITE_AUTH_MODE === "zitadel";

/**
 * File version history (the per-asset "past versions" timeline) is **off**.
 *
 * It's the only feature that needs the GitHub backend in production: it reads
 * old versions from git commit history (`listCommitsForPath` /
 * `getBlobAtCommit`), and the production file backend is Azure Blob (the BFF),
 * which has no version endpoints. With the feature off, the Clock affordance
 * never renders, so users don't hit an empty "not available" modal — and
 * action-items B2/B4 (the BFF GitHub proxy + its secret) aren't needed for it.
 *
 * Re-enabling means either wiring GitHub persistence end-to-end (B2/B4 +
 * DEP-VERSIONING) or building version endpoints on Azure Blob. Flagged rather
 * than deleted so that's a one-line flip, not a rebuild.
 */
export const isVersionHistoryEnabled = (): boolean => false;

/**
 * True when per-workshop records + captured blobs persist to the
 * document-storage BFF (the `workshops` tier) rather than IndexedDB-only.
 * Gated so the R1 demo/offline posture and the seed-mode smoke tests keep
 * working without a live BFF; the cutover flips after the Phase 0 deploy.
 * IndexedDB remains the offline read cache either way.
 */
export const isWorkshopBffEnabled = (): boolean =>
  env.VITE_WORKSHOP_BACKEND === "bff";

/**
 * True when multi-client tenancy is on: an active-client context wraps the
 * app, facilitators get the client picker/switcher + sharing UI, every BFF
 * call carries X-Client-Id (facilitators only), and client-local caches are
 * keyed per client. Off = single-instance behaviour, byte-identical.
 */
export const isMultiClientEnabled = (): boolean =>
  env.VITE_MULTI_CLIENT === "on";
